// Forældreansvarsloven-kursus · lederoversigt
// Viser pr. medarbejder: fremgang, gennemført ja/nej, quizscore (første forsøg og seneste)
// og resultat pr. kapitel. Viser også de spørgsmål, flest svarer forkert på.
//
//   import { mountReport } from './report.js';
//   import COURSE from './content.js';
//   mountReport(document.getElementById('rapport'), { course: COURSE, apiBase: '/api/training' });
//
// Data hentes fra GET {apiBase}/{courseId}/report (se API.md), eller gives direkte som opts.data.

const esc = s => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const pct = (a, b) => (b ? Math.round(a / b * 100) : 0);
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" }) : "–";

export function mountReport(root, opts = {}) {
  const course = opts.course;
  if (!root || !course) throw new Error("mountReport: root og opts.course er påkrævet");
  root.classList.add("fak", "fak-rep");
  root.dataset.theme = opts.theme || "light";

  const S = course.scenes, CH = course.chapters;
  const contentIds = S.filter(s => !s.quiz && !s.end).map(s => s.id);
  const Q = S.filter(s => s.quiz).flatMap(s => s.qs.map(q => ({ ...q, c: s.c, sceneTitle: s.t })));
  const qById = new Map(Q.map(q => [q.id, q]));
  const quizChapters = [...new Set(Q.map(q => q.c))];
  let sortKey = "name", sortDir = 1, selected = null, rows = [];

  function compute(u) {
    const seen = new Set((u.seen || []).filter(id => contentIds.includes(id)));
    const ans = u.answers || {};
    const answeredIds = Object.keys(ans).filter(id => qById.has(id));
    const firstOk = answeredIds.filter(id => ans[id].first?.correct).length;
    const latestOk = answeredIds.filter(id => (ans[id].latest || ans[id].first)?.correct).length;
    const byCh = quizChapters.map(c => {
      const qs = Q.filter(q => q.c === c), a = qs.filter(q => ans[q.id]);
      return { c, total: qs.length, answered: a.length, ok: a.filter(q => ans[q.id].first?.correct).length };
    });
    const progress = pct(seen.size + answeredIds.length, contentIds.length + Q.length);
    return { ...u, seenN: seen.size, answeredN: answeredIds.length, firstOk, latestOk, byCh, progress,
      firstPct: pct(firstOk, answeredIds.length), status: u.completedAt ? "done" : (seen.size || answeredIds.length) ? "going" : "none" };
  }

  function render(data) {
    rows = (data.users || []).map(compute);
    const n = rows.length, done = rows.filter(r => r.status === "done").length;
    const answeredAll = rows.reduce((s, r) => s + r.answeredN, 0), okAll = rows.reduce((s, r) => s + r.firstOk, 0);
    const qStats = Q.map(q => {
      const a = rows.map(r => r.answers?.[q.id]).filter(Boolean);
      return { q, n: a.length, ok: a.filter(x => x.first?.correct).length };
    }).filter(x => x.n > 0).sort((a, b) => a.ok / a.n - b.ok / b.n).slice(0, 6);

    root.innerHTML = `
      <h2>Kursusoversigt</h2>
      <p class="sub">${esc(course.title)} · ${esc(course.source.label)} · opdateret ${fmtDate(data.generatedAt || new Date().toISOString())}</p>
      <div class="fak-kpis">
        <div class="fak-kpi"><div class="l">Medarbejdere</div><div class="v">${n}</div></div>
        <div class="fak-kpi"><div class="l">Har gennemført</div><div class="v">${done}<small>af ${n}</small></div></div>
        <div class="fak-kpi"><div class="l">I gang</div><div class="v">${rows.filter(r => r.status === "going").length}</div></div>
        <div class="fak-kpi"><div class="l">Rigtige i første forsøg</div><div class="v">${pct(okAll, answeredAll)}<small>%</small></div></div>
      </div>
      <div class="fak-tablewrap"><table class="fak-table">
        <thead><tr>
          ${th("name", "Medarbejder")}${th("status", "Status")}${th("progress", "Fremgang")}${th("firstPct", "Første forsøg")}${th("latestOk", "Seneste")}
          <th>Kapitler (første forsøg)</th>${th("lastEventAt", "Senest aktiv")}
        </tr></thead>
        <tbody>${sorted().map(rowHTML).join("") || `<tr><td colspan="7">Ingen har startet kurset endnu.</td></tr>`}</tbody>
      </table></div>
      <div class="fak-legend2"><span><i style="background:var(--fak-teal)"></i>Alle rigtige</span><span><i style="background:#D9A441"></i>Nogle forkerte</span><span><i style="background:var(--fak-bad)"></i>Under halvdelen rigtige</span><span><i style="background:var(--fak-line)"></i>Ikke besvaret</span></div>
      <div class="fak-detailwrap"></div>
      <div class="fak-sec"><h3>Spørgsmål, flest svarer forkert på</h3>
        <ul class="fak-qlist">${qStats.map(x => { const p = pct(x.ok, x.n); return `<li><span class="pct${p < 50 ? " b" : ""}">${p} %</span><span class="q">${esc(x.q.q)}<small>${esc(CH[x.q.c])} · ${x.ok} af ${x.n} rigtige i første forsøg</small></span></li>`; }).join("") || "<li><span></span><span class=\"q\">Ingen svar endnu.</span></li>"}</ul>
      </div>`;
    root.querySelectorAll("th button").forEach(b => b.onclick = () => { const k = b.dataset.k; sortDir = sortKey === k ? -sortDir : (k === "name" ? 1 : -1); sortKey = k; render(data); });
    root.querySelectorAll("tbody tr[data-u]").forEach(tr => tr.onclick = () => { selected = selected === tr.dataset.u ? null : tr.dataset.u; render(data); });
    if (selected) detail(rows.find(r => String(r.userId) === selected));
  }
  const th = (k, l) => `<th><button data-k="${k}" aria-label="Sortér efter ${esc(l)}">${esc(l)}${sortKey === k ? (sortDir > 0 ? " ↑" : " ↓") : ""}</button></th>`;
  function sorted() {
    const order = { done: 2, going: 1, none: 0 };
    return [...rows].sort((a, b) => {
      let x = a[sortKey], y = b[sortKey];
      if (sortKey === "status") { x = order[x]; y = order[y]; }
      if (sortKey === "name") return String(x).localeCompare(String(y), "da") * sortDir;
      return ((x ?? "") > (y ?? "") ? 1 : (x ?? "") < (y ?? "") ? -1 : 0) * sortDir;
    });
  }
  function rowHTML(r) {
    const lab = { done: "Gennemført", going: "I gang", none: "Ikke startet" }[r.status];
    const chCells = r.byCh.map(c => {
      const cls = !c.answered ? "" : c.ok === c.total ? "g" : c.ok / c.total >= .5 ? "m" : "b";
      const t = `${CH[c.c]}: ${c.answered ? `${c.ok} af ${c.total} rigtige` : "ikke besvaret"}`;
      return `<span class="${cls}" title="${esc(t)}" aria-label="${esc(t)}">.</span>`;
    }).join("");
    return `<tr data-u="${esc(r.userId)}" class="${selected === String(r.userId) ? "sel" : ""}">
      <td><span class="fak-name">${esc(r.name)}</span>${r.completedAt ? `<br><small>${fmtDate(r.completedAt)}</small>` : ""}</td>
      <td><span class="fak-pill ${r.status}">${lab}</span></td>
      <td><div class="fak-meter"><span class="t"><i style="width:${r.progress}%"></i></span><span>${r.progress} %</span></div></td>
      <td>${r.answeredN ? `${r.firstOk}/${r.answeredN} · ${r.firstPct} %` : "–"}</td>
      <td>${r.answeredN ? `${r.latestOk}/${r.answeredN}` : "–"}</td>
      <td><div class="fak-chaps">${chCells}</div></td>
      <td>${fmtDate(r.lastEventAt)}</td></tr>`;
  }
  function detail(r) {
    if (!r) return;
    const wrong = Object.entries(r.answers || {}).filter(([id, a]) => qById.has(id) && !a.first?.correct);
    const missingCh = CH.map((c, ci) => ({ c, ids: S.filter(s => s.c === ci && !s.quiz && !s.end).map(s => s.id) }))
      .filter(x => x.ids.length && x.ids.some(id => !(r.seen || []).includes(id)));
    root.querySelector(".fak-detailwrap").innerHTML = `<div class="fak-detail">
      <h4>${esc(r.name)}</h4>
      <p>Har set ${r.seenN} af ${contentIds.length} scener og besvaret ${r.answeredN} af ${Q.length} spørgsmål. Startede ${fmtDate(r.firstEventAt)}.</p>
      ${missingCh.length ? `<p><b>Mangler scener i:</b> ${missingCh.map(x => esc(x.c)).join(", ")}</p>` : ""}
      ${wrong.length ? `<p><b>Forkert i første forsøg:</b></p><ul>${wrong.map(([id, a]) => { const q = qById.get(id); return `<li>${esc(q.q)}<br><span class="no">Svarede: ${esc(q.o[a.first.choice] ?? "?")}</span> · Rigtigt: ${esc(q.o[q.a])}${a.latest?.correct ? " · rettet senere" : ""}</li>`; }).join("")}</ul>` : "<p>Ingen forkerte svar i første forsøg.</p>"}
    </div>`;
  }

  async function load() {
    if (opts.data) { render(opts.data); return; }
    root.innerHTML = `<p>Henter oversigt…</p>`;
    try {
      const r = await fetch(opts.apiBase.replace(/\/$/, "") + "/" + encodeURIComponent(course.id) + "/report", { credentials: "include", ...(opts.fetchInit || {}) });
      if (r.status === 403) { root.innerHTML = `<p>Du har ikke adgang til kursusoversigten. Den kræver administratorrettigheder.</p>`; return; }
      if (!r.ok) throw new Error("HTTP " + r.status);
      render(await r.json());
    } catch (e) {
      root.innerHTML = `<p>Oversigten kunne ikke hentes (${esc(e.message)}). Prøv at genindlæse siden.</p>`;
    }
  }
  load();
  return { reload: load };
}

export default mountReport;
